import { useEffect, useState, useCallback } from 'react';
import { getPreview, createPreview, publishPreview } from '../lib/vibesPreview';

export interface UseVibePreviewOptions {
  /** When true, skip the initial GET preview (avoids 404 for React/bundled projects that use srcdoc). */
  skipFetch?: boolean;
}

interface VibePreviewState {
  previewUrl: string | null;
  loading: boolean;
  error: Error | null;
  publish: () => Promise<void>;
}

export function useVibePreview(
  sessionId: string,
  options: UseVibePreviewOptions = {},
): VibePreviewState {
  const { skipFetch = false } = options;
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const [loading, setLoading] = useState(!skipFetch);
  const [error, setError] = useState<Error | null>(null);

  // Only fetch existing preview when we need a vibes URL (e.g. Rust, or "Open in new tab").
  // Skip fetch for bundleable projects (React etc.) — they use srcdoc; 404 would be normal.
  useEffect(() => {
    if (skipFetch) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const preview = await getPreview(sessionId);
        if (!cancelled) {
          setPreviewUrl(preview?.previewUrl ?? null);
          // 404 → null: treat as "no preview yet / expired", not a fatal error
        }
      } catch (err: any) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, skipFetch]);

  // Listen for external publish events (e.g. Rust UI published successfully)
  useEffect(() => {
    const handler = (ev: Event) => {
      try {
        const detail: any = (ev as CustomEvent)?.detail;
        if (detail?.sessionId === sessionId) {
          setVersion((v) => v + 1);
        }
      } catch (e) {
        void e;
      }
    };
    window.addEventListener('vibes.published', handler as EventListener);
    return () => window.removeEventListener('vibes.published', handler as EventListener);
  }, [sessionId]);

  const publish = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Ensure preview exists before publishing (creates on-demand if needed)
      let preview = await getPreview(sessionId);
      if (!preview) {
        preview = await createPreview(sessionId);
      }
      setPreviewUrl(preview.previewUrl);
      await publishPreview(sessionId);
      setVersion((v) => v + 1);
    } catch (err: any) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const effectiveUrl = previewUrl ? `${previewUrl}?v=${version}` : null;

  return { previewUrl: effectiveUrl, loading, error, publish };
}
